import type { PlanAnchor, PlanEffectEnvelope, ScenePlan } from '@toolproof-core/lib/_contract/scene-plan';

import { planAnchorIndex } from './episode';
import type { EpisodeExcerpt, EpisodeExcerptBounds } from '@/_contract/episodeVisualSource';
import type { TraceReplay } from '@/_contract/trace';

function record(value: unknown, fields: readonly string[]): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid episode excerpt object.');
    const raw = value as Record<string, unknown>;
    for (const key of Object.keys(raw)) {
        if (!fields.includes(key)) throw new Error(`Unknown episode excerpt field '${key}'.`);
    }
    return raw;
}

export function parseEpisodeExcerpt(value: unknown): EpisodeExcerpt | undefined {
    if (value === undefined) return undefined;
    const excerpt = record(value, ['from', 'to']);
    const anchor = (value: unknown): PlanAnchor => {
        const raw = record(value, ['landmark', 'phase']);
        if (typeof raw.landmark !== 'string' || !raw.landmark.trim()) {
            throw new Error('Episode excerpt anchor requires nonempty landmark text.');
        }
        if (raw.phase !== undefined && raw.phase !== 'start' && raw.phase !== 'complete') {
            throw new Error('Episode excerpt anchor phase must be start or complete.');
        }
        return { landmark: raw.landmark, ...(raw.phase === undefined ? {} : { phase: raw.phase }) };
    };
    return { from: anchor(excerpt.from), to: anchor(excerpt.to) };
}

export function resolveEpisodeExcerpt<TEffect extends PlanEffectEnvelope>(
    excerpt: EpisodeExcerpt | undefined, plan: ScenePlan<TEffect>, replay: TraceReplay,
): EpisodeExcerptBounds | undefined {
    if (!excerpt) return undefined;
    const from = planAnchorIndex(replay, excerpt.from);
    const to = planAnchorIndex(replay, excerpt.to);
    if (from < 0 || to < from) throw new Error('Episode excerpt requires ordered, available checkpoints.');
    const within = (anchor: PlanAnchor, context: string) => {
        const index = planAnchorIndex(replay, anchor);
        if (index < from || index > to) throw new Error(`Episode excerpt ${context} is outside its bounds.`);
    };
    for (const beat of plan.beats) {
        within(beat, `beat '${beat.id}'`);
        if (beat.replayFrom) within(beat.replayFrom, `replay '${beat.id}'`);
        for (const cue of beat.cues ?? []) within(cue, `cue '${cue.id}'`);
        for (const segment of beat.presentation ?? []) {
            if (segment.until) within(segment.until, `presentation '${segment.id}'`);
        }
    }
    const last = plan.beats.at(-1);
    if (!last || planAnchorIndex(replay, last) !== to) {
        throw new Error('Episode excerpt final beat must reach its end checkpoint.');
    }
    return { from, to };
}
